import React from 'react';
import useSWR from 'swr';
import {useTranslation} from 'react-i18next';
import api, {getURL} from '../api';
import {usePaymentSettings} from '../hooks';
import {UPSELLING_PAYMENTS_STATUSES} from '../utils/upselling';
import {
  COUNTRY_CODES,
  HOUSING_VERIFICATION_TYPE,
  RESERVATION_PAYMENT_STATUSES,
  SECURITY_DEPOSIT_STATUSES,
} from '../utils/constants';
import {formatOneDate} from '../utils/common';
import {
  checkHasScanDocument,
  checkHasSelfieIdentity,
  getCheckInDate,
  getCheckOutDate,
  getHasGuestMembers,
  getHousingCountryCode,
  getIsAllGuestsRegistered,
  getMembers,
} from '../utils/reservation';
import {useIpDetails} from './ipDetails';
import {useReservation} from './reservation';

type ReservationSource = {id: string; name: string};
type PaymentDetail = {
  name: 'deposit' | 'booking' | 'taxes';
  activated: boolean;
  paid: boolean;
  required: boolean;
};
type PaymentDetails = Record<'deposit' | 'booking' | 'taxes', PaymentDetail>;

type HousingExemption = {
  id: string;
  tax_exempt_sources_nested: ReservationSource[];
  booking_exempt_sources_nested: ReservationSource[];
  deposit_exempt_sources_nested: ReservationSource[];
  extra_service_exempt_sources_nested: ReservationSource[];
};

const INIT_EXEMPTIONS = {
  taxExemption: false,
  bookingPaymentExemption: false,
  depositExemption: false,
};

function getSourceNameExemptions(sourceName = '', exemptions?: HousingExemption) {
  const result = {...INIT_EXEMPTIONS};

  if (!exemptions) {
    return result;
  }

  const findExemption = (source: ReservationSource) => {
    return source.name === sourceName;
  };

  result.taxExemption = Boolean(exemptions.tax_exempt_sources_nested.find(findExemption));
  result.bookingPaymentExemption = Boolean(
    exemptions.booking_exempt_sources_nested.find(findExemption),
  );
  result.depositExemption = Boolean(
    exemptions.deposit_exempt_sources_nested.find(findExemption),
  );

  return result;
}

function fetchCustomFormIdIfRequired(housingId: string) {
  if (!housingId) {
    return null;
  }

  return getURL(
    api.guestForms.ENDPOINTS.customForm(
      `housing_id=${housingId}&field_set=id,required_id_or_passport`,
    ),
  );
}

export type ComputedReservationDetailsContextProps = {
  isLeaderGuest: boolean;
  isReservationLoaded: boolean;
  hasGuestMembers: boolean;
  isAllGuestsRegistered: boolean;
  isDubaiReservation: boolean;
  isUsaReservation: boolean;
  isItalyReservation: boolean;
  isThailandReservation: boolean;
  isIdentityVerificationEnabled: boolean;
  hasBiomatchForGuestLeader: boolean;
  isGreeceReservation: boolean;
  isFranceReservation: boolean;
  isDocScanDisabled: boolean;
  isPoliceActivated: boolean;
  hasTaxes: boolean;
  isContractEnabled: boolean;
  hasDeposits: boolean;
  hadGuestMembersBeforeRegistration: boolean;
  reservation: any;
  areGuestFieldsDisabled: boolean;
  shortCustomForm?: {
    id: string;
    required_id_or_passport: boolean;
  };
  isSomePayments: boolean;
  hasNewPayment: boolean;
  areBookingPaymentsOptional: boolean;
  isGuestPayingPaymentsFees: boolean;
  isGuestPayingTaxesFees: boolean;
  hasBiomatchForAllGuests: boolean;
  hasUpselling: boolean;
  isVerifyDocumentAndSelfie: boolean;
  isVerifyOnlyDocument: boolean;
  isVerifyOnlyDocumentOptional: boolean;
  isBiomatchOnlyForGuestLeader: boolean;
  isAnyPaymentsPending: boolean;
  hasAnyMandatoryPayment: boolean;
  hasNotPayments: boolean;
  isAllPaymentsPaid: boolean;
  hasScanDocument: boolean;
  hasBiomatch: boolean;
  hasBiomatchRequiredCurrentGuest: boolean;
  hasScanDocumentRequiredCurrentGuest: boolean;
  hasBiomatchOptionalCurrentGuest: boolean;
  hasScanDocumentOptionalCurrentGuest: boolean;
  formattedCheckInDate: string;
  formattedCheckOutDate: string;
  housing?: any;
  verificationType?: HOUSING_VERIFICATION_TYPE;
  paymentsDetails?: PaymentDetails;
};

const ComputedReservationDetailsContext =
  React.createContext<ComputedReservationDetailsContextProps>({
    isLeaderGuest: false,
    isReservationLoaded: false,
    hasGuestMembers: false,
    isAllGuestsRegistered: false,
    isDubaiReservation: false,
    isUsaReservation: false,
    isItalyReservation: false,
    isThailandReservation: false,
    isFranceReservation: false,
    isPoliceActivated: false,
    isIdentityVerificationEnabled: false,
    hasBiomatchForGuestLeader: false,
    isGreeceReservation: false,
    isDocScanDisabled: false,
    isAnyPaymentsPending: false,
    hasAnyMandatoryPayment: false,
    hasNotPayments: false,
    isAllPaymentsPaid: false,
    hasTaxes: false,
    isContractEnabled: false,
    hasDeposits: false,
    hadGuestMembersBeforeRegistration: false,
    areGuestFieldsDisabled: false,
    shortCustomForm: {
      id: '',
      required_id_or_passport: false,
    },
    reservation: {},
    isSomePayments: false,
    hasNewPayment: false,
    areBookingPaymentsOptional: false,
    isGuestPayingPaymentsFees: false,
    isGuestPayingTaxesFees: false,
    hasBiomatchForAllGuests: false,
    hasUpselling: false,
    isVerifyDocumentAndSelfie: false,
    isVerifyOnlyDocument: false,
    isVerifyOnlyDocumentOptional: false,
    hasBiomatchRequiredCurrentGuest: false,
    hasScanDocumentRequiredCurrentGuest: false,
    hasBiomatchOptionalCurrentGuest: false,
    hasScanDocumentOptionalCurrentGuest: false,
    isBiomatchOnlyForGuestLeader: false,
    hasScanDocument: false,
    hasBiomatch: false,
    formattedCheckInDate: '',
    formattedCheckOutDate: '',
  });

function ComputedReservationDetailsProvider(props: any) {
  const {data} = useReservation();
  const {i18n} = useTranslation();
  const {ipDetails} = useIpDetails();
  const [housingExemptions, setHousingExemptions] = React.useState<HousingExemption>();
  const {arePaymentSettingsValid} = usePaymentSettings();

  const formattedCheckInDate = formatOneDate({
    date: getCheckInDate(data),
    housingCountry: getHousingCountryCode(data),
    ipCountry: ipDetails?.country_code,
    locale: i18n.language,
  });
  const formattedCheckOutDate = formatOneDate({
    date: getCheckOutDate(data),
    housingCountry: getHousingCountryCode(data),
    ipCountry: ipDetails?.country_code,
    locale: i18n.language,
  });
  const isReservationLoaded = !!data.id;
  const hasGuestMembers = getHasGuestMembers(data);
  const isAllGuestsRegistered = getIsAllGuestsRegistered(data);
  const isLeaderGuest = !hasGuestMembers;
  const isDubaiReservation = getHousingCountryCode(data) === COUNTRY_CODES.uae;
  const isThailandReservation = getHousingCountryCode(data) === COUNTRY_CODES.thailand;
  const isGreeceReservation = getHousingCountryCode(data) === COUNTRY_CODES.greece;
  const isUsaReservation = getHousingCountryCode(data) === COUNTRY_CODES.usa;
  const isItalyReservation = getHousingCountryCode(data) === COUNTRY_CODES.italy;
  const isFranceReservation = getHousingCountryCode(data) === COUNTRY_CODES.france;
  const isPoliceActivated = data?.housing?.is_auto_police_registration_enabled;
  const isIdentityVerificationEnabled = Boolean(
    data?.housing?.is_self_online_check_in_enabled,
  );
  const verificationType = data?.housing?.verification_type as
    | HOUSING_VERIFICATION_TYPE
    | undefined;

  const isVerifyOnlyDocument =
    isIdentityVerificationEnabled &&
    (verificationType === HOUSING_VERIFICATION_TYPE.mandatoryDocumentOnly ||
      verificationType === HOUSING_VERIFICATION_TYPE.optionalDocumentOnly);

  const isVerifyOnlyDocumentOptional =
    isIdentityVerificationEnabled &&
    verificationType === HOUSING_VERIFICATION_TYPE.optionalDocumentOnly;

  const isVerifyDocumentAndSelfie =
    isIdentityVerificationEnabled &&
    (verificationType === HOUSING_VERIFICATION_TYPE.mandatoryDocumentAndSelfie ||
      verificationType === HOUSING_VERIFICATION_TYPE.optionalDocumentAndSelfie);

  const isDocScanDisabled = !Boolean(data?.housing?.is_checkin_online_doc_scan_enabled);

  const hasBiomatchForGuestLeader = isLeaderGuest && isIdentityVerificationEnabled;
  const hasBiomatchForAllGuests = Boolean(
    data?.housing?.is_biometric_match_for_all_enabled,
  );

  const isBiomatchOnlyForGuestLeader = Boolean(
    isIdentityVerificationEnabled && !data?.housing?.is_biometric_match_for_all_enabled,
  );
  const isBiomatchForAllGuests = Boolean(
    isIdentityVerificationEnabled && data?.housing?.is_biometric_match_for_all_enabled,
  );

  const hasBiomatch = checkHasSelfieIdentity({
    isLeaderGuest,
    isVerifyDocumentAndSelfie,
    isBiomatchForAllGuests,
  });

  const hasScanDocument = checkHasScanDocument({
    isLeaderGuest,
    isVerifyOnlyDocument,
    isBiomatchForAllGuests,
  });

  const hasBiomatchRequiredCurrentGuest =
    hasBiomatch &&
    verificationType === HOUSING_VERIFICATION_TYPE.mandatoryDocumentAndSelfie;
  const hasScanDocumentRequiredCurrentGuest =
    hasScanDocument &&
    verificationType === HOUSING_VERIFICATION_TYPE.mandatoryDocumentOnly;
  const hasBiomatchOptionalCurrentGuest =
    hasBiomatch &&
    verificationType === HOUSING_VERIFICATION_TYPE.optionalDocumentAndSelfie;
  const hasScanDocumentOptionalCurrentGuest =
    hasScanDocument &&
    verificationType === HOUSING_VERIFICATION_TYPE.optionalDocumentOnly;

  const isContractEnabled = Boolean(data?.housing?.is_contract_enabled);
  const hadGuestMembersBeforeRegistration = getMembers(data).length > 1;
  const areGuestFieldsDisabled = Boolean(data?.housing?.are_guest_fields_disabled);

  const housingId = data?.housing?.id;
  const fetchHousingExemptions = React.useCallback(async () => {
    const {data} = await api.housingExemptions.getById(housingId);

    if (data) {
      setHousingExemptions(data);
    }
  }, [housingId]);

  React.useEffect(() => {
    if (housingId) {
      fetchHousingExemptions();
    }
  }, [fetchHousingExemptions, housingId]);

  const exemptions = React.useMemo(() => {
    const sourceName = data?.source_name;
    return getSourceNameExemptions(sourceName, housingExemptions);
  }, [data, housingExemptions]);

  const isDepositActivated =
    !exemptions.depositExemption &&
    data?.housing?.security_deposit_status &&
    data?.housing?.security_deposit_status !== SECURITY_DEPOSIT_STATUSES.inactive;
  const isDepositPaid =
    isDepositActivated &&
    (data?.security_deposit?.status === 'CONFIRMED' ||
      data?.security_deposit?.status === 'RELEASED' ||
      data?.security_deposit_amount === '0.00');
  const areDepositsRequired =
    arePaymentSettingsValid &&
    !exemptions.depositExemption &&
    data?.housing?.security_deposit_status === SECURITY_DEPOSIT_STATUSES.mandatory;
  const hasDeposits = Boolean(
    arePaymentSettingsValid &&
      isDepositActivated &&
      data?.security_deposit?.status !== 'CONFIRMED' &&
      data?.security_deposit?.status !== 'RELEASED' &&
      data?.security_deposit_amount !== '0.00',
  );

  const isTaxesActivated = Boolean(
    !exemptions.taxExemption && data?.tax_activated && data?.housing?.seasons?.length,
  );
  const isTaxesPaid = Boolean(isTaxesActivated && data?.have_taxes_been_paid);
  const hasTaxes = Boolean(
    arePaymentSettingsValid && isTaxesActivated && !data?.have_taxes_been_paid,
  );

  const {data: shortCustomForms} = useSWR(fetchCustomFormIdIfRequired(housingId));
  const shortCustomForm = shortCustomForms?.[0];
  const isSomePayments = Boolean(parseFloat(data?.total_amount_to_pay || '0'));
  const isSomeGuestAdded = Boolean(data?.guest_group?.members?.length);
  const hasNewPayment = isSomePayments && isSomeGuestAdded;
  const isGuestPayingPaymentsFees =
    data?.housing?.commission_responsibility_for_booking === 'GUEST';
  const isGuestPayingTaxesFees =
    data?.housing?.commission_responsibility_for_tourist_tax === 'GUEST';
  const areBookingPaymentsActivated =
    !exemptions.bookingPaymentExemption &&
    data?.housing?.reservation_payments_status !== 'INACTIVE';
  const isBookingPaymentPaid = Boolean(
    areBookingPaymentsActivated && data?.have_payments_been_paid,
  );
  const areBookingPaymentsOptional =
    data?.housing?.reservation_payments_status === RESERVATION_PAYMENT_STATUSES.optional;
  const areBookingPaymentsRequired =
    data?.housing?.reservation_payments_status === RESERVATION_PAYMENT_STATUSES.mandatory;

  const hasUpselling =
    isReservationLoaded &&
    data?.housing?.upselling_payments_status === UPSELLING_PAYMENTS_STATUSES.active;

  const payments = React.useMemo<PaymentDetails>(
    () => ({
      taxes: {
        name: 'taxes',
        activated: isTaxesActivated,
        paid: isTaxesPaid,
        required: true,
      },
      booking: {
        name: 'booking',
        activated: areBookingPaymentsActivated,
        paid: isBookingPaymentPaid,
        required: areBookingPaymentsRequired,
      },
      deposit: {
        name: 'deposit',
        activated: isDepositActivated,
        paid: isDepositPaid,
        required: areDepositsRequired,
      },
    }),
    [
      areBookingPaymentsActivated,
      areDepositsRequired,
      areBookingPaymentsRequired,
      isBookingPaymentPaid,
      isDepositActivated,
      isDepositPaid,
      isTaxesActivated,
      isTaxesPaid,
    ],
  );

  const avabilityPayments = React.useMemo(
    () => Object.values(payments).filter(payment => payment.activated),
    [payments],
  );

  const hasNotPayments = !avabilityPayments.length;
  const hasAnyMandatoryPayment = React.useMemo(
    () => avabilityPayments.some(payment => !payment.paid && payment.required),
    [avabilityPayments],
  );
  const isAnyPaymentsPending = React.useMemo(
    () => !hasNotPayments && avabilityPayments.some(payment => !payment.paid),
    [avabilityPayments, hasNotPayments],
  );
  const isAllPaymentsPaid = React.useMemo(
    () => !hasNotPayments && avabilityPayments.every(payment => payment.paid),
    [avabilityPayments, hasNotPayments],
  );

  return (
    <ComputedReservationDetailsContext.Provider
      {...props}
      value={{
        hasAnyMandatoryPayment,
        isAnyPaymentsPending,
        isAllPaymentsPaid,
        hasNotPayments,
        isLeaderGuest,
        isPoliceActivated,
        isReservationLoaded,
        hasBiomatch,
        hasScanDocument,
        verificationType,
        hasBiomatchRequiredCurrentGuest,
        hasScanDocumentRequiredCurrentGuest,
        hasBiomatchOptionalCurrentGuest,
        hasScanDocumentOptionalCurrentGuest,
        isVerifyDocumentAndSelfie,
        isVerifyOnlyDocumentOptional,
        isVerifyOnlyDocument,
        isBiomatchOnlyForGuestLeader,
        areGuestFieldsDisabled,
        hasTaxes,
        hasGuestMembers,
        isAllGuestsRegistered,
        isDubaiReservation,
        isThailandReservation,
        isFranceReservation,
        isUsaReservation,
        isItalyReservation,
        isIdentityVerificationEnabled,
        hasBiomatchForGuestLeader,
        isGreeceReservation,
        isDocScanDisabled,
        isContractEnabled,
        hasDeposits,
        hadGuestMembersBeforeRegistration,
        shortCustomForm,
        isSomePayments,
        hasNewPayment,
        areBookingPaymentsOptional,
        isGuestPayingPaymentsFees,
        isGuestPayingTaxesFees,
        hasBiomatchForAllGuests,
        hasUpselling,
        formattedCheckInDate,
        formattedCheckOutDate,
        reservation: data,
        paymentsDetails: payments,
      }}
    />
  );
}

function useComputedReservationDetails() {
  const context = React.useContext(ComputedReservationDetailsContext);
  if (context === undefined) {
    throw new Error('useReservation must be used within a ReservationProvider');
  }
  return context;
}

export {
  ComputedReservationDetailsContext,
  ComputedReservationDetailsProvider,
  useComputedReservationDetails,
};
